// src/pages/ProtokollUebersicht.jsx
// Buero/Admin-Uebersicht aller Protokolle mit "an Kunden verschickt"-Haekchen.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, withTimeout } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { C, zurueckStil } from './formui';
import ProtokollFormPage from './ProtokollFormPage';

// Einheitliche Status-Schilder (wie Startseite und Protokoll). "vorbereitet"
// und "zugewiesen" fehlten - geplante Wartungen standen als "Entwurf" da.
const STATUS_LABELS = {
  'vorbereitet':          { label: 'geplant',          color: C.gray700,    bg: C.gray100 },
  'zugewiesen':           { label: 'geplant',          color: C.gray700,    bg: C.gray100 },
  'entwurf':              { label: 'in Arbeit',        color: C.blue,       bg: C.blueBg },
  'monteur_abgeschlossen':{ label: 'wartet auf Büro',  color: C.orangeText, bg: C.orangeBg },
  'buero_freigegeben':    { label: 'freigegeben',      color: C.green,      bg: C.greenBg },
  'abgeschlossen':        { label: 'abgeschlossen',    color: C.green,      bg: C.greenBg },
};

const FILTER_OPTIONS = [
  { value: 'offen',    label: '🔴 Offen (nicht verschickt)' },
  { value: 'alle',     label: 'Alle' },
  { value: 'entwurf',  label: 'Entwürfe' },
  { value: 'monteur_abgeschlossen', label: 'Monteur fertig' },
  { value: 'buero_freigegeben', label: 'Büro freigegeben' },
  { value: 'verschickt', label: '✓ Verschickt' },
];

function formatDatum(iso) {
  if (!iso) return '–';
  try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return '–'; }
}
function formatDatetime(iso) {
  if (!iso) return '–';
  try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '–'; }
}

export default function ProtokollUebersicht({ onBack }) {
  const { user } = useAuth();
  const [protokolle, setProtokolle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('offen');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [openProtokollId, setOpenProtokollId] = useState(null);
  const [ausgewaehlt, setAusgewaehlt] = useState(new Set());
  const [sammelLaeuft, setSammelLaeuft] = useState(null);
  // Sammel-Loeschen mit eigenem Bestaetigungsfenster (Pruefung 20.09.2026):
  // Liste der betroffenen Protokolle + bewusstes Haekchen statt eines OK-Dialogs.
  const [loeschFrage, setLoeschFrage] = useState(null);
  const [loeschBestaetigt, setLoeschBestaetigt] = useState(false);

  // "Laedt ..." nur beim ersten Mal - danach bleibt die Liste stehen (Regel aus
  // CLAUDE.md: Nachladen darf die Seite nie abraeumen).
  const ersteLadung = useRef(true);
  const load = useCallback(async () => {
    if (ersteLadung.current) setLoading(true);
    setError(null);
    try {
      // Seitenweise: PostgREST liefert hoechstens 1000 Zeilen je Anfrage -
      // aeltere Protokolle fehlten sonst still.
      const alle = [];
      for (let von = 0; ; von += 1000) {
        const { data, error: e } = await withTimeout(supabase
          .from('protokolle')
          .select('id, status, art, wartungs_datum, stationsbezeich, kunde_name, kunde_id, station_id, erstellt_am, erstellt_von_id, geaendert_am, an_kunden_verschickt_am, an_kunden_verschickt_von, stationen(bezeichnung, kunden(name, kunden_nr))')
          .order('geaendert_am', { ascending: false }).order('id').range(von, von + 999), 30000, 'Protokolle laden');
        if (e) throw e;
        alle.push(...(data || []));
        if (!data || data.length < 1000) break;
      }
      setProtokolle(alle);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
      ersteLadung.current = false;
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Wenn ein Protokoll geoeffnet wurde, Protokoll-Form Page rendern
  if (openProtokollId) {
    return <ProtokollFormPage protokollId={openProtokollId} onBack={() => { setOpenProtokollId(null); load(); }} />;
  }

  const filtered = protokolle.filter(p => {
    if (filter === 'alle') {}
    else if (filter === 'verschickt') { if (!p.an_kunden_verschickt_am) return false; }
    else if (filter === 'offen') { if (p.an_kunden_verschickt_am) return false; }
    else { if (p.status !== filter) return false; }
    if (search.trim()) {
      const s = search.toLowerCase();
      const hayName = (p.stationen?.kunden?.name || p.kunde_name || '').toLowerCase();
      const hayStation = (p.stationen?.bezeichnung || p.stationsbezeich || '').toLowerCase();
      const hayKnr = (p.stationen?.kunden?.kunden_nr || '').toLowerCase();
      if (!hayName.includes(s) && !hayStation.includes(s) && !hayKnr.includes(s)) return false;
    }
    return true;
  });

  const toggleVerschickt = async (p) => {
    const newAm = p.an_kunden_verschickt_am ? null : new Date().toISOString();
    const payload = {
      an_kunden_verschickt_am: newAm,
      an_kunden_verschickt_von: newAm ? user?.id : null,
    };
    // Optimistic update
    setProtokolle(prev => prev.map(x => x.id === p.id ? { ...x, ...payload } : x));
    try {
      // Das Ergebnis MUSS auf error geprueft werden: Supabase wirft bei einem
      // abgelehnten Speichern nicht, es liefert { error }. Vorher blieb der
      // Haken dann sichtbar, obwohl in der Datenbank nichts stand.
      const { error: e } = await withTimeout(
        supabase.from('protokolle').update(payload).eq('id', p.id),
        15000,
        'Versendet-Status speichern'
      );
      if (e) throw e;
    } catch (err) {
      alert('Fehler beim Speichern: ' + err.message);
      load();
    }
  };

  // ── Mehrfachauswahl ────────────────────────────────────────────────────────
  // Vorher war das Kaestchen links KEINE Auswahl, sondern der Schalter
  // "an Kunden verschickt". Anna-Lena wollte Protokolle markieren, um sie zu
  // loeschen, und hat damit 21 Stueck als verschickt markiert. Jetzt ist das
  // Kaestchen eine Auswahl, und "verschickt" ist ein sichtbarer Knopf.
  const umschalten = (id) => {
    setAusgewaehlt(alt => {
      const neu = new Set(alt);
      if (neu.has(id)) neu.delete(id); else neu.add(id);
      return neu;
    });
  };
  const alleSichtbarenWaehlen = () => {
    setAusgewaehlt(alt =>
      alt.size === filtered.length ? new Set() : new Set(filtered.map(p => p.id))
    );
  };
  const auswahlLeeren = () => setAusgewaehlt(new Set());

  const gewaehlte = filtered.filter(p => ausgewaehlt.has(p.id));

  // Sammelaktion mit gemeinsamer Fehlerbehandlung
  const sammelAktion = async (beschriftung, ausfuehren) => {
    setSammelLaeuft(beschriftung);
    try {
      await ausfuehren();
      auswahlLeeren();
      await load();
    } catch (err) {
      alert('Fehler bei "' + beschriftung + '": ' + (err.message || 'Unbekannt'));
    } finally {
      setSammelLaeuft(null);
    }
  };

  const alsVerschicktMarkieren = (markieren) => sammelAktion(
    markieren ? 'Als verschickt markieren' : 'Markierung entfernen',
    async () => {
      const ids = gewaehlte.map(p => p.id);
      const { error: e } = await withTimeout(
        supabase.from('protokolle').update({
          an_kunden_verschickt_am: markieren ? new Date().toISOString() : null,
          an_kunden_verschickt_von: markieren ? (user?.id ?? null) : null,
        }).in('id', ids),
        30000, 'Sammelaktion'
      );
      if (e) throw e;
    }
  );

  const freigeben = () => {
    const passend = gewaehlte.filter(p => p.status === 'monteur_abgeschlossen');
    if (passend.length === 0) {
      alert('Freigeben geht nur bei Protokollen, die der Monteur abgeschlossen hat.\n\n'
        + 'In deiner Auswahl ist keines davon.');
      return;
    }
    if (!window.confirm(passend.length + ' Protokoll(e) freigeben?')) return;
    return sammelAktion('Freigeben', async () => {
      const { error: e } = await withTimeout(
        supabase.from('protokolle').update({
          status: 'buero_freigegeben',
          vom_buero_freigegeben_am: new Date().toISOString(),
          vom_buero_freigegeben_von: user?.id ?? null,
        }).in('id', passend.map(p => p.id)),
        30000, 'Freigeben'
      );
      if (e) throw e;
    });
  };

  const loeschenFragen = () => { setLoeschBestaetigt(false); setLoeschFrage(gewaehlte); };
  const loeschen = () => {
    const ids = (loeschFrage || []).map(p => p.id);
    setLoeschFrage(null);
    if (ids.length === 0) return;
    return sammelAktion('Löschen', async () => {
      // Maengel zuerst: sie haengen mit ON DELETE SET NULL am Protokoll und
      // wuerden sonst als herrenlose Eintraege zurueckbleiben.
      // Fehler pruefen: Vorher lief das Loeschen der Protokolle auch dann
      // weiter, wenn die Maengel gar nicht geloescht werden durften.
      const { error: eM } = await withTimeout(
        supabase.from('maengel').delete().in('protokoll_id', ids),
        30000, 'Mängel löschen'
      );
      if (eM) throw eM;
      const { data: geloescht, error: e } = await withTimeout(
        supabase.from('protokolle').delete().in('id', ids).select('id'),
        30000, 'Protokolle löschen'
      );
      if (e) throw e;
      const weg = (geloescht || []).map(p => p.id);
      if (weg.length < ids.length) {
        throw new Error((ids.length - weg.length) + ' Protokoll(e) konnten nicht gelöscht werden (keine Berechtigung?).');
      }
      // Erst DANACH die Fotos der wirklich geloeschten Protokolle: Die Foto-
      // Eintraege haengen nicht fest am Protokoll und blieben sonst in der
      // Stations-Galerie stehen.
      if (weg.length) {
        const { data: fotoZeilen, error: eF } = await withTimeout(
          supabase.from('protokoll_fotos').delete().in('protokoll_id', weg).select('storage_path'),
          30000, 'Fotos löschen');
        if (!eF) {
          const pfade = (fotoZeilen || []).map(f => f.storage_path).filter(Boolean);
          if (pfade.length) {
            try { await supabase.storage.from('fotos').remove(pfade); } catch { /* Dateien bleiben notfalls liegen */ }
          }
        }
      }
    });
  };

  // Zähler
  const counts = {
    alle: protokolle.length,
    offen: protokolle.filter(p => !p.an_kunden_verschickt_am).length,
    verschickt: protokolle.filter(p => !!p.an_kunden_verschickt_am).length,
    entwurf: protokolle.filter(p => p.status === 'entwurf').length,
    monteur_abgeschlossen: protokolle.filter(p => p.status === 'monteur_abgeschlossen').length,
    buero_freigegeben: protokolle.filter(p => p.status === 'buero_freigegeben').length,
  };

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif" }}>
      {/* Header */}
      <div style={{ background: C.navy, padding: '14px', boxShadow: '0 2px 14px rgba(0,0,0,0.22)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={onBack} style={zurueckStil}>‹ Zurück</button>
          <div style={{ color: C.white, fontWeight: 800, fontSize: 16 }}>📋 Protokoll-Übersicht</div>
          <div style={{ flex: 1 }}/>
          <div style={{ color: C.gray300, fontSize: 14 }}>{filtered.length} von {protokolle.length} Protokollen</div>
        </div>
      </div>

      {/* Filter + Suche */}
      <div style={{ background: C.white, padding: '10px 14px', borderBottom: '1px solid ' + C.gray200 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Suche: Kunde, Station, Kundennr ..."
            style={{ flex: '1 1 220px', padding: '8px 12px', border: '1.5px solid ' + C.gray200, borderRadius: 7, fontSize: 15, outline: 'none' }}/>
          <button onClick={load} style={{ padding: '8px 14px', background: C.gray100, color: C.navy, border: 'none', borderRadius: 7, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>🔄 Neu laden</button>
        </div>
        <div style={{ maxWidth: 1100, margin: '8px auto 0', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {FILTER_OPTIONS.map(o => {
            const c = counts[o.value] ?? 0;
            return (
              <button key={o.value} onClick={() => setFilter(o.value)}
                style={{ padding: '5px 11px', borderRadius: 18, border: '1.5px solid ' + (filter === o.value ? C.navy : C.gray200), background: filter === o.value ? C.navy : C.white, color: filter === o.value ? C.yellow : C.gray700, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {o.label} <span style={{ opacity: 0.7 }}>({c})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Liste */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '14px' }}>
        {loading && <div style={{ padding: 24, color: C.gray500, textAlign: 'center' }}>Lade Protokolle...</div>}
        {error && <div style={{ padding: 12, background: C.redBg, color: C.red, borderRadius: 8, marginBottom: 12 }}>! {error}</div>}
        {/* Alle sichtbaren auswaehlen */}
        {!loading && filtered.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px 14px', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: C.gray700 }}>
            <input type="checkbox" checked={ausgewaehlt.size === filtered.length && filtered.length > 0}
              onChange={alleSichtbarenWaehlen}
              style={{ width: 24, height: 24, cursor: 'pointer', accentColor: C.navy }}/>
            Alle {filtered.length} angezeigten auswählen
          </label>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: C.gray500, fontStyle: 'italic' }}>
            Keine Protokolle in dieser Auswahl.
          </div>
        )}
        {!loading && filtered.map(p => {
          const statusCfg = STATUS_LABELS[p.status] || STATUS_LABELS.entwurf;
          const verschickt = !!p.an_kunden_verschickt_am;
          return (
            <div key={p.id} style={{ background: verschickt ? '#F0F8F0' : C.white, border: '1.5px solid ' + (verschickt ? C.green : C.gray200), borderRadius: 10, padding: 12, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {/* Auswahl fuer Sammelaktionen - bewusst neutral, damit es nicht
                  mit dem Verschickt-Zustand verwechselt wird. */}
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: 4 }} onClick={e => e.stopPropagation()}>
                <input type="checkbox" checked={ausgewaehlt.has(p.id)} onChange={() => umschalten(p.id)}
                  aria-label="Protokoll auswählen"
                  style={{ width: 26, height: 26, cursor: 'pointer', accentColor: C.navy }}/>
              </label>
              <div style={{ flex: 1, minWidth: 220, cursor: 'pointer' }} onClick={() => setOpenProtokollId(p.id)}>
                <div style={{ fontWeight: 800, color: C.navy, fontSize: 14 }}>
                  {p.stationen?.kunden?.name || p.kunde_name || '(Kunde unbekannt)'}
                  {p.stationen?.kunden?.kunden_nr && <span style={{ fontWeight: 500, color: C.gray500, marginLeft: 6 }}>· #{p.stationen.kunden.kunden_nr}</span>}
                </div>
                <div style={{ fontSize: 15, color: C.gray700, marginTop: 2 }}>
                  {p.stationen?.bezeichnung || p.stationsbezeich || '(Station unbekannt)'}
                  <span style={{ color: C.gray500 }}> · {p.art === 'sichtpruefung' ? 'Sichtprüfung' : 'Wartung'} am {formatDatum(p.wartungs_datum)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <span style={{ background: statusCfg.bg, color: statusCfg.color, padding: '3px 10px', borderRadius: 12, fontSize: 13, fontWeight: 800 }}>
                  {statusCfg.label}
                </span>
                {/* "Verschickt" ist eine Handlung, kein Zustand zum Ankreuzen -
                    deshalb ein beschrifteter Knopf statt eines Kaestchens. */}
                <button type="button" onClick={(e) => { e.stopPropagation(); toggleVerschickt(p); }}
                  style={{ minHeight: 36, padding: '6px 12px', borderRadius: 7, cursor: 'pointer',
                           fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
                           background: verschickt ? C.greenBg : C.white,
                           color: verschickt ? C.green : C.gray700,
                           border: '1.5px solid ' + (verschickt ? C.green : C.gray200) }}>
                  {verschickt ? '✓ verschickt am ' + formatDatum(p.an_kunden_verschickt_am) : 'Als verschickt markieren'}
                </button>
                {!verschickt && p.geaendert_am && (
                  <span style={{ color: C.gray500, fontSize: 12 }}>geändert {formatDatetime(p.geaendert_am)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Aktionsleiste: erscheint erst, wenn etwas ausgewaehlt ist. Unten, weil
          dort der Daumen hinkommt und weil sie so nicht dauernd im Weg ist. */}
      {ausgewaehlt.size > 0 && (
        <div style={{ position: 'sticky', bottom: 0, background: C.navy, borderTop: '3px solid ' + C.navyLight,
                      padding: '14px 16px', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
                      boxShadow: '0 -6px 24px rgba(10,22,40,.3)', zIndex: 60 }}>
          <span style={{ color: C.white, fontWeight: 800, fontSize: 15, marginRight: 4 }}>
            {ausgewaehlt.size} ausgewählt
          </span>
          <button type="button" onClick={auswahlLeeren} disabled={!!sammelLaeuft}
            style={{ minHeight: 44, padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700,
                     background: 'none', color: C.gray300, border: '1.5px solid ' + C.navyLight }}>
            Auswahl aufheben
          </button>
          {/* Loeschen bewusst weit weg von "Freigeben" (links statt direkt daneben)
              und nur als Umriss - ein Fehlgriff loeschte sonst ganze Protokolle. */}
          <button type="button" onClick={loeschenFragen} disabled={!!sammelLaeuft}
            style={{ minHeight: 44, padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700,
                     background: 'none', color: '#FF9C8F', border: '1.5px solid #FF9C8F' }}>
            Löschen …
          </button>
          <div style={{ flex: 1 }}/>
          <button type="button" onClick={() => alsVerschicktMarkieren(true)} disabled={!!sammelLaeuft}
            style={{ minHeight: 44, padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700,
                     background: C.white, color: C.navy, border: 'none' }}>
            Als verschickt markieren
          </button>
          <button type="button" onClick={() => alsVerschicktMarkieren(false)} disabled={!!sammelLaeuft}
            style={{ minHeight: 44, padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700,
                     background: 'none', color: C.white, border: '1.5px solid ' + C.navyLight }}>
            Markierung entfernen
          </button>
          <button type="button" onClick={freigeben} disabled={!!sammelLaeuft}
            style={{ minHeight: 44, padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700,
                     background: C.green, color: C.white, border: 'none' }}>
            Freigeben
          </button>
          {sammelLaeuft && (
            <span style={{ color: C.yellow, fontSize: 14, fontWeight: 700, width: '100%' }}>
              {sammelLaeuft} läuft …
            </span>
          )}
        </div>
      )}
      {loeschFrage && (() => {
        const fertige = loeschFrage.filter(p => ['monteur_abgeschlossen', 'buero_freigegeben', 'abgeschlossen'].includes(p.status));
        const n = loeschFrage.length;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', zIndex: 400,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
            <div style={{ background: C.white, borderRadius: 12, padding: 18, width: '100%', maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.red }}>
                {n === 1 ? '1 Protokoll endgültig löschen?' : n + ' Protokolle endgültig löschen?'}
              </div>
              <div style={{ fontSize: 14, color: C.gray700, margin: '6px 0 10px', lineHeight: 1.45 }}>
                Mitgelöscht werden alle Prüfpunkte, Fotos und Mängel dieser Protokolle.
                Das lässt sich <strong>nicht rückgängig</strong> machen.
              </div>
              {fertige.length > 0 && (
                <div style={{ background: C.redBg, borderLeft: '4px solid ' + C.red, borderRadius: 6, padding: '8px 12px',
                              fontSize: 14, color: C.navy, fontWeight: 700, marginBottom: 10 }}>
                  Achtung: {fertige.length === 1 ? '1 davon ist' : fertige.length + ' davon sind'} bereits abgeschlossen
                  oder freigegeben – mit Unterschrift und Bewertung.
                </div>
              )}
              <div style={{ overflowY: 'auto', border: '1px solid ' + C.gray200, borderRadius: 8, marginBottom: 12 }}>
                {loeschFrage.map(p => (
                  <div key={p.id} style={{ padding: '7px 10px', borderBottom: '1px solid ' + C.gray100, fontSize: 14 }}>
                    <strong style={{ color: C.navy }}>{p.stationen?.kunden?.name || p.kunde_name || '(Kunde unbekannt)'}</strong>
                    <span style={{ color: C.gray700 }}> · {p.stationen?.bezeichnung || p.stationsbezeich || '(Station unbekannt)'}</span>
                    <span style={{ color: C.gray500 }}> · {formatDatum(p.wartungs_datum)} · {(STATUS_LABELS[p.status] || STATUS_LABELS.entwurf).label}</span>
                  </div>
                ))}
              </div>
              <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 15, fontWeight: 700, color: C.navy, cursor: 'pointer', marginBottom: 12 }}>
                <input type="checkbox" checked={loeschBestaetigt} onChange={e => setLoeschBestaetigt(e.target.checked)}
                  style={{ width: 24, height: 24, accentColor: C.red }}/>
                Ja, {n === 1 ? 'dieses Protokoll' : 'diese ' + n + ' Protokolle'} endgültig löschen
              </label>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setLoeschFrage(null)}
                  style={{ minHeight: 44, padding: '10px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 800,
                           background: C.navy, color: C.white, border: 'none' }}>
                  Abbrechen – nichts löschen
                </button>
                <button type="button" onClick={loeschen} disabled={!loeschBestaetigt}
                  style={{ minHeight: 44, padding: '10px 18px', borderRadius: 8, fontSize: 15, fontWeight: 800,
                           cursor: loeschBestaetigt ? 'pointer' : 'not-allowed',
                           background: loeschBestaetigt ? C.red : C.gray200, color: loeschBestaetigt ? C.white : C.gray500, border: 'none' }}>
                  Endgültig löschen
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
